/**
 * CONSAI — Login Page
 * Design: "CONSAI Executive Suite" dark theme (matches uploaded HTML design)
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [emailOrMobile, setEmailOrMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // Parallax effect for background glow spots
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      const spots = document.querySelectorAll('.login-glow-spot');
      spots.forEach((spot, index) => {
        const depth = (index + 1) * 20;
        const moveX = (x - 0.5) * depth;
        const moveY = (y - 0.5) * depth;
        spot.style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailOrMobile || !password) {
      toast.error(language === 'hi' ? 'कृपया सभी फ़ील्ड भरें' : 'Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      const result = await login(emailOrMobile, password, rememberMe);
      if (result.success) {
        toast.success(t('loginSuccess'));
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || t('loginFailed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden"
      style={{ backgroundColor: '#131313', color: '#ffffff', WebkitFontSmoothing: 'antialiased' }}
      data-testid="login-page"
    >
      {/* Atmospheric Background Glow */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
        <div className="login-glow-spot" style={{ position: 'absolute', background: '#ff571a', width: '600px', height: '600px', top: '-300px', left: '-100px', borderRadius: '50%', filter: 'blur(120px)', opacity: 0.15 }} />
        <div className="login-glow-spot" style={{ position: 'absolute', background: '#00e475', width: '500px', height: '500px', bottom: '-200px', right: '10%', borderRadius: '50%', filter: 'blur(120px)', opacity: 0.08 }} />
        <div className="login-glow-spot" style={{ position: 'absolute', background: '#c6c6c7', width: '400px', height: '400px', top: '20%', right: '-100px', borderRadius: '50%', filter: 'blur(120px)', opacity: 0.05 }} />
      </div>

      {/* Top Navigation Bar */}
      <header style={{ backgroundColor: '#131313', zIndex: 50, position: 'relative' }}>
        <nav className="flex justify-between items-center py-4" style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 16px', paddingLeft: '16px', paddingRight: '16px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
            <img src="/consai-logo.png" alt="CONSAI" style={{ height: '28px', width: 'auto' }} />
            <span className="horizon-font tracking-widest" style={{ fontSize: '17px' }}>
              <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
            </span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="/" style={{ color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '16px', textDecoration: 'none' }} className="hover:text-white transition-colors">Portal</a>
            <a href="/" style={{ color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '16px', textDecoration: 'none' }} className="hover:text-white transition-colors">Insights</a>
            <a href="/" style={{ color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '16px', textDecoration: 'none' }} className="hover:text-white transition-colors">Documentation</a>
          </div>
          <div className="flex items-center gap-4">
            <button style={{ color: '#c4c7c8', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '16px' }} className="hover:text-white transition-colors">
              Help
            </button>
            <span className="material-symbols-outlined" style={{ color: '#ffffff', cursor: 'pointer', fontSize: '22px' }}>language</span>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center py-12 relative" style={{ padding: '48px 16px', zIndex: 1 }}>
        <div className="login-card-glow" style={{ maxWidth: '440px', width: '100%' }}>
          <div style={{ background: '#111111', border: '1px solid #262626', borderRadius: '12px', padding: '32px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', position: 'relative', overflow: 'hidden' }}>

            {/* Card Header: CONSAI logo box + language toggle */}
            <div className="flex justify-between items-start" style={{ marginBottom: '40px' }}>
              <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', background: '#050505', padding: '6px 10px', border: '1px solid #262626', borderRadius: '4px' }}>
                <img src="/consai-logo.png" alt="CONSAI" style={{ height: '20px', width: 'auto' }} />
                <span className="horizon-font tracking-widest" style={{ fontSize: '13px' }}>
                  <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
                </span>
              </a>
              <button
                onClick={toggleLanguage}
                style={{ color: '#c4c7c8', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px', fontWeight: '500' }}
                className="hover:text-white transition-colors"
                data-testid="language-toggle"
              >
                {language === 'en' ? 'हिंदी' : 'English'}
              </button>
            </div>

            {/* Card Title */}
            <div style={{ marginBottom: '32px' }}>
              <h1 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '24px', lineHeight: '32px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                {language === 'hi' ? 'वापस स्वागत है' : 'Welcome back'}
              </h1>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '16px', color: '#c4c7c8' }}>
                {language === 'hi' ? 'अपने व्यवसाय का प्रबंधन जारी रखें' : 'Continue managing your business'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

              {/* Email / Mobile */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="emailOrMobile"
                  style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: '16px', letterSpacing: '0.05em', fontWeight: '700', textTransform: 'uppercase', color: '#c4c7c8', display: 'block' }}
                >
                  {t('emailOrMobile')}
                </label>
                <div style={{ position: 'relative' }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#c4c7c8', fontSize: '20px', pointerEvents: 'none', transition: 'color 0.2s' }}
                  >mail</span>
                  <input
                    id="emailOrMobile"
                    type="text"
                    value={emailOrMobile}
                    onChange={(e) => setEmailOrMobile(e.target.value)}
                    placeholder={language === 'hi' ? 'ईमेल या मोबाइल नंबर' : 'name@company.com'}
                    style={{ width: '100%', background: '#050505', border: '1px solid #262626', borderRadius: '8px', padding: '14px 16px 14px 48px', color: '#ffffff', fontFamily: 'Manrope, sans-serif', fontSize: '16px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                    onFocus={(e) => { e.target.style.borderColor = '#ffffff'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#262626'; }}
                    data-testid="login-email-mobile-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="password"
                  style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', lineHeight: '16px', letterSpacing: '0.05em', fontWeight: '700', textTransform: 'uppercase', color: '#c4c7c8', display: 'block' }}
                >
                  {t('password')}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ width: '100%', background: '#050505', border: '1px solid #262626', borderRadius: '8px', padding: '14px 48px 14px 16px', color: '#ffffff', fontFamily: 'Manrope, sans-serif', fontSize: '16px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                    onFocus={(e) => { e.target.style.borderColor = '#ffffff'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#262626'; }}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#c4c7c8', padding: 0 }}
                    className="hover:text-white transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Remember me + Forgot password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer" style={{ userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    style={{ width: '16px', height: '16px', background: '#050505', borderColor: '#262626', borderRadius: '4px', accentColor: '#ffffff', cursor: 'pointer' }}
                    data-testid="remember-me-checkbox"
                  />
                  <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8' }}>{t('rememberMe')}</span>
                </label>
                <Link
                  to="/forgot-password"
                  style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#ff571a', fontWeight: '500', textDecoration: 'none' }}
                  className="hover:brightness-125 transition-all"
                  data-testid="forgot-password-link"
                >
                  {t('forgotPassword')}
                </Link>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', background: '#ffffff', color: '#000000', fontWeight: '700', padding: '16px', borderRadius: '8px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '16px', opacity: loading ? 0.85 : 1, boxShadow: '0 0 20px rgba(255,255,255,0.1)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = loading ? '0.85' : '1'; }}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin" style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {language === 'hi' ? 'लॉगिन हो रहा है...' : 'Logging in...'}
                  </>
                ) : t('login')}
              </button>

              {/* OR Divider */}
              <div style={{ position: 'relative', margin: '16px 0 0' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', borderTop: '1px solid #262626' }} />
                </div>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '700' }}>
                  <span style={{ background: '#111111', padding: '0 16px', color: 'rgba(196,199,200,0.6)', fontFamily: 'Manrope, sans-serif' }}>
                    {language === 'hi' ? 'या' : 'OR CONTINUE WITH'}
                  </span>
                </div>
              </div>

              {/* Google Sign In */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                style={{ width: '100%', background: '#050505', border: '1px solid #262626', color: '#ffffff', fontWeight: '500', padding: '14px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', transition: 'all 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1A1A1A'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#050505'; }}
                data-testid="google-signin-button"
              >
                <svg style={{ width: '20px', height: '20px' }} viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.28 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {t('signInWithGoogle')}
              </button>
            </form>

            {/* Card Footer */}
            <div style={{ marginTop: '40px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8' }}>
                {t('noAccount')}{' '}
                <Link
                  to="/signup"
                  style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'none' }}
                  className="hover:underline"
                  data-testid="signup-link"
                >
                  {t('signup')}
                </Link>
              </p>
              <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(196,199,200,0.4)', fontWeight: '700', fontFamily: 'Manrope, sans-serif' }}>
                © 2026 BUFFETISM IT VENTURES PVT LTD
              </p>
            </div>

          </div>
        </div>
      </main>

      {/* Global Footer */}
      <footer style={{ backgroundColor: '#131313', borderTop: '1px solid #262626', position: 'relative', zIndex: 1 }}>
        <div className="flex flex-col md:flex-row justify-between items-center py-8" style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px', gap: '16px' }}>
          <div className="flex flex-col items-center md:items-start gap-2">
            <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
              <img src="/consai-logo.png" alt="CONSAI" style={{ height: '22px', width: 'auto' }} />
              <span className="horizon-font tracking-widest" style={{ fontSize: '14px' }}>
                <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
              </span>
            </a>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8', opacity: 0.8 }}>© 2026 CONSAI AI Business Management Suite. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {['Privacy Policy', 'Terms of Service', 'Security', 'Contact Support'].map((item) => (
              <a
                key={item}
                href="#"
                style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8', textDecoration: 'none', opacity: 0.8, transition: 'all 0.2s' }}
                className="hover:text-white hover:opacity-100 transition-all"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
